import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLoading } from '../../contexts/LoadingContext';
import { optimizeImageUrl } from '../../utils/optimizeImageUrl';
import { 
  getHeroImageForCategory, 
  getOptimizedHeroUrl 
} from '../../utils/heroImageUtils';
import logoImage from '../../assets/logo.png';

interface AppSplashScreenProps {
  children: React.ReactNode;
}

const AppSplashScreen: React.FC<AppSplashScreenProps> = ({ children }) => {
  const { 
    isSplashVisible, 
    hideSplash, 
    progress, 
    updateProgress, 
    startSimulation,
    phase, 
    updatePhase,
    isLooping,
    setIsLooping
  } = useLoading();
  
  const [internalVisible, setInternalVisible] = useState(true);
  const isInitialLoad = useRef(true);

  const preloadData = useCallback(async () => {
    setIsLooping(true); // Start repetitive loading
    try {
      // Phase 1: Fetch categories (0% → 30%)
      updatePhase('categories');
      // startSimulation(30, 1500); // We're looping so we don't need fixed simulation thresholds as much

      const { categoryService } = await import('../../services/categoryService');
      const categories = await categoryService.getActiveCategories();
      // updateProgress(30);

      // Phase 2: Fetch tours (30% → 60%)
      updatePhase('tours');
      const { tourService } = await import('../../services/tourService');
      const tours = await tourService.getAllTours(false); 
      // updateProgress(60);

      // Phase 3: Preload hero images (60% → 100%)
      updatePhase('images');
      const heroImageUrls: string[] = [];
      const priorityCategories = categories.slice(0, 4);
      for (const cat of priorityCategories) {
        const rawUrl = getHeroImageForCategory(cat, tours);
        if (rawUrl) {
          heroImageUrls.push(getOptimizedHeroUrl(rawUrl));
        }
      }

      if (heroImageUrls.length > 0) {
        let loadedCount = 0;
        const total = heroImageUrls.length;
        
        await Promise.all(heroImageUrls.map(url => {
          return new Promise((resolve) => {
            const img = new Image();
            img.src = url;
            const handleLoad = () => {
              loadedCount++;
              resolve(true);
            };
            img.onload = handleLoad;
            img.onerror = handleLoad; 
            if (img.complete) handleLoad();
          });
        }));
      }

      setIsLooping(false); // Stop looping and finish
      updateProgress(100);
      updatePhase('done');
    } catch (err) {
      console.warn('Splash preload error:', err);
      setIsLooping(false);
      updateProgress(100);
    } finally {
      // Short pause for impact
      await new Promise<void>((r) => setTimeout(r, 400));
      setInternalVisible(false);
      hideSplash();
    }
  }, [updatePhase, updateProgress, hideSplash, setIsLooping]);

  useEffect(() => {
    if (isInitialLoad.current) {
      isInitialLoad.current = false;
      preloadData();
    }
  }, [preloadData]);

  // Handle re-triggering (e.g. going back to home)
  useEffect(() => {
    if (isSplashVisible && !internalVisible) {
      setInternalVisible(true);
      preloadData();
    }
  }, [isSplashVisible, internalVisible, preloadData]);

  const phaseLabel: Record<string, string> = {
    categories: 'Loading categories...',
    tours: 'Fetching tours...',
    images: 'Preparing your experience...',
    done: 'Almost ready!',
  };

  const isActuallyReady = !internalVisible && !isSplashVisible;

  return (
    <>
      {/* App content — render behind the splash so it's ready to show instantly */}
      <div
        className={`transition-opacity duration-500 ${isActuallyReady ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
        aria-hidden={!isActuallyReady}
      >
        {children}
      </div>

      {/* Splash overlay */}
      <AnimatePresence>
        {internalVisible && (
          <motion.div
            key="splash"
            initial={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.55, ease: 'easeOut' }}
            className="fixed inset-0 z-[99999] flex flex-col items-center justify-center bg-white dark:bg-slate-950"
          >
            {/* Pulsing & Revolving Logo */}
            <motion.div
              className="mb-12 relative"
              animate={{ 
                scale: [1, 1.05, 1],
                rotate: [0, 0, 360, 360], // Rotate 360 degrees every interval
              }}
              transition={{ 
                scale: { duration: 2, repeat: Infinity, ease: "easeInOut" },
                rotate: { 
                  duration: 2, // Duration of the rotation itself
                  repeat: Infinity, 
                  repeatDelay: 8, // Total 10s cycle (2s rotation + 8s delay)
                  ease: "easeInOut" 
                }
              }}
            >
              <img
                src={logoImage}
                alt="SightTrails Safaris"
                className="w-48 h-48 sm:w-56 sm:h-56 md:w-64 md:h-64 object-contain drop-shadow-[0_0_15px_rgba(42,126,206,0.2)]"
              />
            </motion.div>

            {/* Enhanced Progress Bar Track */}
            <div className="relative w-72 sm:w-96 bg-slate-100 dark:bg-slate-800/40 rounded-full h-3 overflow-hidden shadow-[inset_0_2px_4px_rgba(0,0,0,0.1)] dark:shadow-[inset_0_2px_4px_rgba(0,0,0,0.4)] border border-gray-200/50 dark:border-slate-800">
              {/* Primary Progress Fill with Glow */}
              <motion.div
                className="absolute left-0 top-0 h-full rounded-full shadow-[0_0_15px_rgba(42,126,206,0.5)]"
                style={{
                  background: 'linear-gradient(90deg, #2A7ECE, #5BA8EF, #3EBDD3)',
                }}
                animate={isLooping ? { 
                  width: ['0%', '100%'],
                  left: ['0%', '0%'] 
                } : { 
                  width: `${progress}%` 
                }}
                transition={isLooping ? { 
                  duration: 1.2, // Faster repetitive loading
                  repeat: Infinity, 
                  ease: "easeInOut" 
                } : { 
                  duration: 0.4, 
                  ease: 'easeOut' 
                }}
              />

              {/* Ultra-Visible Scanner Overlay */}
              <motion.div
                className="absolute top-0 h-full w-full opacity-80"
                style={{
                  background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.6), transparent)',
                  width: '40%',
                }}
                animate={{ 
                  left: ['-40%', '140%'],
                }}
                transition={{ 
                  duration: 1.0, 
                  repeat: Infinity, 
                  ease: "linear",
                }}
              />

              {/* Vivid Glow Pulse */}
              <motion.div
                className="absolute top-0 h-full w-1/3 opacity-50 bg-white blur-md"
                animate={{ 
                  left: ['-33%', '133%'],
                }}
                transition={{ 
                  duration: 0.6, 
                  repeat: Infinity, 
                  ease: "easeInOut",
                  delay: 0.1
                }}
              />
            </div>

            {/* Status Text */}
            <AnimatePresence mode="wait">
              {phase && (
                <motion.p
                  key={phase}
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -6 }}
                  transition={{ duration: 0.25 }}
                  className="mt-4 text-[11px] font-semibold uppercase tracking-widest text-slate-400 dark:text-slate-500 select-none"
                >
                  {phaseLabel[phase] || phase}
                </motion.p>
              )}
            </AnimatePresence>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default AppSplashScreen;
